import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { useState } from "react";
import superjson from "superjson";
import { clearDeviceSession, getDeviceToken } from "~/lib/device-session";
import { trpc } from "~utils/trpc/query-client";

const API_URL =
	typeof window !== "undefined"
		? (window as any).ENV?.API_URL || "http://localhost:4000"
		: process.env.API_URL || "http://localhost:4000";

// A revoked or invalid device token makes every call 401; drop the dead session
// and send the device back to the provisioning wall.
function handleAuthError(error: unknown) {
	if ((error as { data?: { code?: string } })?.data?.code !== "UNAUTHORIZED") return;
	if (typeof window === "undefined" || window.location.pathname === "/provision") return;
	clearDeviceSession();
	window.location.assign("/provision");
}

export function CheckinTRPCProvider({ children }: { children: React.ReactNode }) {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: { queries: { staleTime: 10_000, retry: false } },
				queryCache: new QueryCache({ onError: handleAuthError }),
				mutationCache: new MutationCache({ onError: handleAuthError }),
			}),
	);
	const [client] = useState(() =>
		trpc.createClient({
			links: [
				httpBatchLink({
					url: API_URL,
					transformer: superjson,
					headers() {
						const token = getDeviceToken();
						return token ? { Authorization: `Bearer ${token}` } : {};
					},
				}),
			],
		}),
	);

	return (
		<QueryClientProvider client={queryClient}>
			<trpc.Provider client={client} queryClient={queryClient}>
				{children}
			</trpc.Provider>
		</QueryClientProvider>
	);
}
